'use client'

import { Pause, Play, Volume2 } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

interface InlineAudioPlayerProps {
  audioUrl: string
  className?: string
  variant?: 'inline' | 'popover'
}

export default function InlineAudioPlayer({
  audioUrl,
  className,
  variant = 'inline',
}: InlineAudioPlayerProps) {
  const audioRef = React.useRef<HTMLAudioElement>(null)
  const progressRef = React.useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [duration, setDuration] = React.useState(0)

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  const togglePlay = React.useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      void audio.play()
    } else {
      audio.pause()
    }
  }, [])

  const seekToPosition = React.useCallback(
    (clientX: number) => {
      const audio = audioRef.current
      const progressBar = progressRef.current
      if (!audio || !progressBar || duration <= 0) return

      const rect = progressBar.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      audio.currentTime = ratio * duration
    },
    [duration]
  )

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    seekToPosition(event.clientX)
  }

  const handleProgressKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || duration <= 0) return

    const step = event.key === 'ArrowLeft' ? -5 : event.key === 'ArrowRight' ? 5 : 0
    if (!step) return

    event.preventDefault()
    audio.currentTime = Math.min(duration, Math.max(0, audio.currentTime + step))
  }

  if (!audioUrl) return null

  return (
    <div
      className={cn(
        'not-prose inline-flex max-w-full items-center gap-2 rounded-full border border-primary/20 bg-gradient-to-r from-primary/[0.07] via-primary/[0.04] to-transparent px-2 py-1.5 shadow-sm backdrop-blur-sm',
        variant === 'popover' && 'w-full rounded-xl px-3 py-2.5',
        className
      )}
    >
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {isPlaying ? <Pause className="size-3.5" /> : <Play className="ml-0.5 size-3.5" />}
      </button>

      <div className={cn('flex min-w-0 flex-1 items-center gap-2', variant === 'inline' && 'w-28 sm:w-36')}>
        <div
          ref={progressRef}
          role="slider"
          tabIndex={0}
          aria-label="Audio progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          onClick={handleProgressClick}
          onKeyDown={handleProgressKeyDown}
          className="group relative h-1.5 flex-1 cursor-pointer overflow-hidden rounded-full bg-primary/15"
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
          <div
            className="absolute top-1/2 size-2.5 -translate-y-1/2 rounded-full bg-primary opacity-0 shadow transition-opacity group-hover:opacity-100"
            style={{ left: `calc(${progress}% - 0.3125rem)` }}
          />
        </div>

        <span className="shrink-0 text-[10px] font-medium tabular-nums text-muted-foreground">
          {formatAudioTime(currentTime)}
          {duration > 0 ? ` / ${formatAudioTime(duration)}` : ''}
        </span>
      </div>

      <Volume2 className="size-3.5 shrink-0 text-primary/50" aria-hidden />

      <audio
        ref={audioRef}
        preload="metadata"
        className="hidden"
        src={audioUrl}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false)
          setCurrentTime(0)
        }}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
      >
        Your browser does not support the audio element.
      </audio>
    </div>
  )
}
